import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { from, Observable, switchMap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiService } from '../api.service';
import {
  InboxNotificationResponse,
  MarkAsReadResponse,
  MarkAllAsReadResponse,
  NotificationPreferencesResponse,
  UpdatePreferencesRequest,
  UpdatePreferencesResponse,
  GetInboxNotificationsParams
} from 'src/app/models/core/inbox-notification.model';

@Injectable({
  providedIn: 'root'
})
export class InboxService {
  private baseUrl = `${environment.api_server}/api`;

  constructor(
    private http: HttpClient,
    private _api: ApiService
  ) { }


  getNotifications(params: GetInboxNotificationsParams = {}): Observable<InboxNotificationResponse> {
    const url = `${this.baseUrl}/notifications`;

    let httpParams = new HttpParams();

    httpParams = httpParams.set('per_page', (params.per_page || 10).toString());

    if (params.page) {
      httpParams = httpParams.set('page', params.page.toString());
    }

    if (params.category) {
      httpParams = httpParams.set('category', params.category);
    }

    if (params.type_notification) {
      httpParams = httpParams.set('type_notification', params.type_notification);
    }

    if (params.status) {
      httpParams = httpParams.set('status', params.status);
    }

    if (params.unread_only) {
      httpParams = httpParams.set('unread_only', 'true');
    }

    if (params.read_only) {
      httpParams = httpParams.set('read_only', 'true');
    }

    return from(this._api.getAuthHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<InboxNotificationResponse>(url, { headers, params: httpParams })
      )
    );
  }

  markAsRead(notificationId: number, context?: HttpContext): Observable<MarkAsReadResponse> {
    const url = `${this.baseUrl}/notifications/${notificationId}/read`;

    return from(this._api.getAuthHeaders()).pipe(
      switchMap((headers) =>
        this.http.put<MarkAsReadResponse>(url, {}, { headers, context })
      )
    );
  }

  markMultipleAsRead(notificationIds: number[]): Observable<MarkAsReadResponse> {
    const url = `${this.baseUrl}/notifications/mark-multiple-read`;
    const body = { notification_ids: notificationIds };

    return from(this._api.getAuthHeaders()).pipe(
      switchMap((headers) =>
        this.http.put<MarkAsReadResponse>(url, body, { headers })
      )
    );
  }

  markAllAsRead(): Observable<MarkAllAsReadResponse> {
    const url = `${this.baseUrl}/notifications/mark-all-read`;

    return from(this._api.getAuthHeaders()).pipe(
      switchMap((headers) =>
        this.http.put<MarkAllAsReadResponse>(url, {}, { headers })
      )
    );
  }

  getPreferences(): Observable<NotificationPreferencesResponse> {
    const url = `${this.baseUrl}/notifications/preferences`;

    return from(this._api.getAuthHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<NotificationPreferencesResponse>(url, { headers })
      )
    );
  }

  updatePreferences(request: UpdatePreferencesRequest): Observable<UpdatePreferencesResponse> {
    const url = `${this.baseUrl}/notifications/preferences`;

    return from(this._api.getAuthHeaders()).pipe(
      switchMap((headers) =>
        this.http.put<UpdatePreferencesResponse>(url, request, { headers })
      )
    );
  }

  getUnreadCount(): Observable<{success: boolean, unread_count: number}> {
    const url = `${this.baseUrl}/notifications/unread-count`;

    return from(this._api.getAuthHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<{success: boolean, unread_count: number}>(url, { headers })
      )
    );
  }

}
